import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import i18n from '../i18n';
import { cache } from '../utils/cache';
import { 
  loginApi, 
  registerApi, 
  startBiometricLoginApi, 
  finishBiometricLoginApi,
  startBiometricRegistrationApi,
  finishBiometricRegistrationApi,
  getBiometricStatusApi,
  revokeBiometricCredentialApi
} from '../api/auth';
import { tenantApi } from '../api/tenant';

/** Owner-editable farm profile fields as stored in session.farm. */
const farmProfileFrom = (tenant) => ({
  address: tenant?.address || '',
  contactPhone: tenant?.contactPhone || '',
  contactEmail: tenant?.contactEmail || '',
  logoData: tenant?.logoData || '',
});
import { profileApi } from '../api/profile';
import { billingApi } from '../api/billing';
import { getErrorMessage } from '../api/client';
import { 
  isBiometricSupported, 
  isPlatformAuthenticatorAvailable,
  rememberBiometricEmail,
  registerBiometricCredential,
  authenticateWithBiometric,
  getBiometricErrorMessage,
  cacheRegistrationOptions,
  getCachedRegistrationOptions,
  clearCachedRegistrationOptions,
  generateRegistrationQRCode,
  parseRegistrationQRCode,
  isRetryableError
} from '../utils/biometric';

const AuthContext = createContext(null);
const STORAGE_KEY = 'farm_smartly_session';

// If subscriptionEndsAt is set and has passed → downgrade to free
function applyExpiry(session) {
  if (!session) return null;
  if ((session.plan === 'pro' || session.plan === 'growth') && session.subscriptionEndsAt) {
    if (Date.now() > session.subscriptionEndsAt) {
      return { ...session, plan: 'free', subscriptionEndsAt: null };
    }
  }
  return session;
}

// True when the JWT's `exp` claim is in the past. Unreadable tokens are treated
// as not-expired so the server stays the final authority on them.
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // A session left over from yesterday has a dead token. Drop it up front so
    // the app lands on /login immediately instead of mounting the dashboard and
    // waiting on a burst of 401s followed by a hard redirect.
    if (parsed?.token && isTokenExpired(parsed.token)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return applyExpiry(parsed);
  } catch {
    return null;
  }
}

function saveSession(session) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else         localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());

  // Sync i18n language when session is loaded from storage
  useEffect(() => {
    const storedSession = loadSession();
    if (storedSession?.user?.language) {
      i18n.changeLanguage(storedSession.user.language);
    }
  }, []);

  // Periodically re-check subscription expiry (every minute)
  useEffect(() => {
    const id = setInterval(() => {
      setSession((prev) => {
        if (!prev) return prev;
        const checked = applyExpiry(prev);
        if (checked !== prev) {
          saveSession(checked);
          return checked;
        }
        return prev;
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Refresh plan limits on startup and whenever the tab becomes visible again
  // (covers mobile PWA wake-up where visibility event may not fire, and stale cached sessions)
  const refreshTenantSession = useCallback(() => {
    setSession((current) => {
      if (!current) return current; // not logged in — skip
      tenantApi.getMe().then((tenant) => {
        setSession((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            plan: tenant.plan || prev.plan,
            billingCycle: tenant.billingCycle || prev.billingCycle,
            subscriptionEndsAt: tenant.subscriptionEndsAt
              ? new Date(tenant.subscriptionEndsAt).getTime()
              : prev.subscriptionEndsAt,
            farm: {
              ...prev.farm,
              planLimits: tenant.planLimits || prev.farm?.planLimits || {},
              subUserLimit: tenant.subUserLimit ?? prev.farm?.subUserLimit ?? 1,
              milkUnit: tenant.milkUnit || prev.farm?.milkUnit || 'L',
              milkDensity: tenant.milkDensity ?? prev.farm?.milkDensity ?? 1.03,
              ...farmProfileFrom(tenant),
            },
          };
          saveSession(next);
          return next;
        });
      }).catch(() => { /* non-fatal */ });
      return current;
    });
  }, []);

  // Run once on mount to populate fresh plan/subUserLimit for PWA sessions loaded from cache
  useEffect(() => {
    refreshTenantSession();
  }, [refreshTenantSession]);

  // Also refresh when the tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshTenantSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshTenantSession]);

  // Internal: set session state + persist + clear cache
  const login = useCallback((sessionData) => {
    cache.clear();
    saveSession(sessionData);
    setSession(sessionData);
    // Sync i18n language with user's preference
    if (sessionData?.user?.language && i18n.language !== sessionData.user.language) {
      i18n.changeLanguage(sessionData.user.language);
    }
    // The login payload doesn't carry tenant settings (milk unit etc.) — pull them in now.
    refreshTenantSession();
  }, [refreshTenantSession]);

  const logout = useCallback(() => {
    cache.clear();
    saveSession(null);
    setSession(null);
  }, []);

  // ── API-backed auth actions ──────────────────────────────────────────────

  const loginWithCredentials = useCallback(async (email, password) => {
    const sessionData = await loginApi(email, password);
    login(sessionData);
    return sessionData;
  }, [login]);

  const registerWithCredentials = useCallback(async (formData) => {
    // formData: { farmName, email, password, fullName, country }
    const sessionData = await registerApi(formData);
    login(sessionData);
    return sessionData;
  }, [login]);

  // ── Biometric Authentication ─────────────────────────────────────────────

  const loginWithBiometric = useCallback(async (email) => {
    // Step 1: Start biometric authentication with server
    const options = await startBiometricLoginApi(email);
    
    // Step 2: Perform biometric authentication with device
    const credentialData = await authenticateWithBiometric(options);
    
    // Include challenge in credential data for server verification
    credentialData.challenge = options.challenge;
    
    // Step 3: Complete authentication with server
    const sessionData = await finishBiometricLoginApi(credentialData);
    rememberBiometricEmail(email);
    login(sessionData);
    return sessionData;
  }, [login]);

  const registerBiometric = useCallback(async (email, password, friendlyName) => {
    // Step 1: Start registration with server (verifies password)
    const options = await startBiometricRegistrationApi(email, password, friendlyName);
    
    // Step 2: Register credential with device
    const credentialData = await registerBiometricCredential(options);
    
    // Include challenge in credential data for server verification
    credentialData.challenge = options.challenge;
    credentialData.friendlyName = friendlyName;
    
    // Step 3: Complete registration with server
    const sessionData = await finishBiometricRegistrationApi(credentialData);
    rememberBiometricEmail(email);
    login(sessionData);
    return sessionData;
  }, [login]);

  const getBiometricStatus = useCallback(async () => {
    if (!session?.token) return { hasBiometricCredentials: false, credentialCount: 0, credentials: [] };
    return await getBiometricStatusApi(session.token);
  }, [session?.token]);

  const revokeBiometricCredential = useCallback(async (credentialId, reason) => {
    if (!session?.token) throw new Error('Not authenticated');
    await revokeBiometricCredentialApi(credentialId, reason, session.token);
  }, [session?.token]);

  // ── Profile / tenant updates ─────────────────────────────────────────────

  const updateFarm = useCallback(async (farmData) => {
    // Optimistic local update
    setSession((prev) => {
      const next = { ...prev, farm: { ...prev.farm, ...farmData } };
      saveSession(next);
      return next;
    });
    // Persist to API (profile fields: '' clears, omitted = unchanged)
    const tenant = await tenantApi.updateMe({
      name: farmData.name,
      country: farmData.country,
      address: farmData.address,
      contactPhone: farmData.contactPhone,
      contactEmail: farmData.contactEmail,
      logoData: farmData.logoData,
    });
    return tenant;
  }, []);

  /**
   * Milk measurement settings (OWNER only). API first, then session — unlike updateFarm, a rejected
   * change must not leave the UI showing a unit the server never accepted.
   */
  const updateMilkSettings = useCallback(async ({ milkUnit, milkDensity }) => {
    const tenant = await tenantApi.updateMe({ name: session?.farm?.name, milkUnit, milkDensity });
    setSession((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        farm: {
          ...prev.farm,
          milkUnit: tenant.milkUnit || milkUnit || prev.farm?.milkUnit || 'L',
          milkDensity: tenant.milkDensity ?? milkDensity ?? prev.farm?.milkDensity ?? 1.03,
        },
      };
      saveSession(next);
      return next;
    });
    return tenant;
  }, [session?.farm?.name]);

  const updateUser = useCallback(async (userData) => {
    // Optimistic local update
    setSession((prev) => {
      const next = { ...prev, user: { ...prev.user, ...userData } };
      saveSession(next);
      return next;
    });
    // Persist to API — map name → fullName for backend
    const req = {};
    if (userData.name     !== undefined) req.fullName = userData.name;
    if (userData.phone    !== undefined) req.phone    = userData.phone;
    if (userData.language !== undefined) {
      req.language = userData.language;
      // Sync i18n language immediately
      i18n.changeLanguage(userData.language);
    }
    await profileApi.updateMe(req);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await profileApi.changePassword(currentPassword, newPassword);
  }, []);

  /**
   * Call after successful Razorpay payment.
   * Posts the payment to the backend, then re-fetches tenant to get
   * the authoritative plan, subscriptionEndsAt, and planLimits.
   */
  const upgradePlan = useCallback(async (plan, billingCycle = 'monthly', paymentInfo = {}) => {
    cache.clear();
    // Post to backend — paymentInfo: { amount, currency, providerPaymentId, providerOrderId }
    const updatedTenant = await tenantApi.subscribe({
      plan,
      billingCycle,
      amount:            paymentInfo.amount      || 0,
      currency:          paymentInfo.currency    || 'USD',
      providerPaymentId: paymentInfo.razorpay_payment_id || null,
      providerOrderId:   paymentInfo.razorpay_order_id   || null,
    });

    // Patch session with authoritative values from backend response
    setSession((prev) => {
      const next = {
        ...prev,
        plan:               updatedTenant.plan,
        billingCycle:       updatedTenant.billingCycle || prev.billingCycle,
        subscribedAt:       updatedTenant.subscribedAt     ? new Date(updatedTenant.subscribedAt).getTime()     : null,
        subscriptionEndsAt: updatedTenant.subscriptionEndsAt ? new Date(updatedTenant.subscriptionEndsAt).getTime() : null,
        farm: {
          ...prev.farm,
          planLimits: updatedTenant.planLimits || prev.farm?.planLimits || {},
          subUserLimit: updatedTenant.subUserLimit ?? prev.farm?.subUserLimit ?? 1,
        },
      };
      saveSession(next);
      return next;
    });
  }, []);

  /**
   * Complete upgrade using new Order API flow.
   * Verifies payment and activates subscription.
   * Called after successful Razorpay checkout.
   */
  /** Patch the session from a plan-activation result (paid verify result, or the result of a free promo order). */
  const applyUpgradeResult = useCallback((result) => {
    cache.clear();
    setSession((prev) => {
      const next = {
        ...prev,
        plan:               result.tenant?.plan || prev.plan,
        billingCycle:       result.tenant?.billingCycle || prev.billingCycle,
        subscribedAt:       result.tenant?.subscribedAt     ? new Date(result.tenant.subscribedAt).getTime()     : null,
        subscriptionEndsAt: result.subscriptionEndsAt ? new Date(result.subscriptionEndsAt).getTime() : null,
        farm: {
          ...prev.farm,
          planLimits: result.tenant?.planLimits || prev.farm?.planLimits || {},
          subUserLimit: result.tenant?.subUserLimit ?? prev.farm?.subUserLimit ?? 1,
        },
      };
      saveSession(next);
      return next;
    });
    return result;
  }, []);

  const completeUpgrade = useCallback(async (razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    // Verify payment with backend, then patch the session with its authoritative values
    const result = await billingApi.verifyPayment(razorpayOrderId, razorpayPaymentId, razorpaySignature);
    return applyUpgradeResult(result);
  }, [applyUpgradeResult]);

  /**
   * Re-fetch tenant from backend and sync planLimits + plan into session.
   * Call this on app resume or after admin changes free plan limits server-side.
   */
  const refreshPlanLimits = useCallback(async () => {
    try {
      const tenant = await tenantApi.getMe();
      setSession((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          plan:               tenant.plan || prev.plan,
          billingCycle:       tenant.billingCycle || prev.billingCycle,
          subscriptionEndsAt: tenant.subscriptionEndsAt ? new Date(tenant.subscriptionEndsAt).getTime() : prev.subscriptionEndsAt,
          farm: {
            ...prev.farm,
            planLimits: tenant.planLimits || prev.farm?.planLimits || {},
            subUserLimit: tenant.subUserLimit ?? prev.farm?.subUserLimit ?? 1,
            milkUnit: tenant.milkUnit || prev.farm?.milkUnit || 'L',
            milkDensity: tenant.milkDensity ?? prev.farm?.milkDensity ?? 1.03,
            ...farmProfileFrom(tenant),
          },
        };
        saveSession(next);
        return next;
      });
    } catch {
      // Non-fatal — session stays as-is
    }
  }, []);

  /**
   * Update activeModules in the session after a farm module is activated or deactivated.
   * Pass an array of module type strings, e.g. ['POULTRY', 'DAIRY'].
   */
  const updateActiveModules = useCallback((moduleNames) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next = { ...prev, activeModules: moduleNames };
      saveSession(next);
      return next;
    });
  }, []);

  const value = {
    session,
    isLoggedIn:          !!(session?.token),
    user:                session?.user              || {},
    farm:                session?.farm              || {},
    timezone:            session?.user?.timezone    || 'UTC',
    language:            session?.user?.language    || 'en',
    plan:                session?.plan              || 'free',
    billingCycle:        session?.billingCycle       || null,
    subscriptionEndsAt:  session?.subscriptionEndsAt || null,
    subscribedAt:        session?.subscribedAt       || null,
    token:               session?.token              || null,
    activeModules:       session?.activeModules      || ['POULTRY'],
    // Auth actions
    login,
    logout,
    loginWithCredentials,
    registerWithCredentials,
    loginWithSession: login, // Alias for direct session setting (e.g., after email verification)
    // Data update actions
    updateFarm,
    updateMilkSettings,
    updateUser,
    changePassword,
    upgradePlan,
    completeUpgrade,
    applyUpgradeResult,
    refreshPlanLimits,
    updateActiveModules,
    planLimits: session?.farm?.planLimits || {},
    // Biometric authentication
    loginWithBiometric,
    registerBiometric,
    getBiometricStatus,
    revokeBiometricCredential,
    // Biometric utilities
    isBiometricSupported,
    isPlatformAuthenticatorAvailable,
    getBiometricErrorMessage,
    // Offline & QR code utilities
    cacheRegistrationOptions,
    getCachedRegistrationOptions,
    clearCachedRegistrationOptions,
    generateRegistrationQRCode,
    parseRegistrationQRCode,
    isRetryableError,
    // Utility
    getErrorMessage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
